import React, { useState } from 'react';
import { collection, doc, setDoc, Timestamp } from 'firebase/firestore';
import styled, { useTheme } from 'styled-components';
import { useFirestoreQuery } from '@react-query-firebase/firestore';
import toast from 'react-hot-toast';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { canvasToBuffer, ImageFormat, loadImageOnCanvas } from 'services/utility/image/resizeImage';
import { processImage } from 'services/utility/image/imageProcessor';
import { resizeAndCropToKeepAspect } from 'services/utility/image/imageProcessingPipelines';
import {
  PROFILE_IMAGE_HEIGHT,
  PROFILE_IMAGE_WIDTH,
  COVER_IMAGE_HEIGHT,
  COVER_IMAGE_WIDTH,
} from 'app-constants/profileAndCoverImagesSize';
import { validateDisplayName } from 'validations';

import { RoleValue } from '../../../../types';
import {
  AbsoluteImage,
  Box,
  DropZoneContainer,
  FlexContainer,
  Image,
  Text,
} from '../../../../shared/Typhography';
import { Button, Input, SelectForm, Splash } from '../../../../shared/Components';
import { RegistrationField, registrationShema } from '../../../../validations/createAccount';
import { useForm } from '../../../../hooks';
import { Password, User } from '../../../../assets/LoginPage';
import {
  /* auth, */
  DEFAULT_COVER_IMAGE,
  DEFAULT_PROFILE_IMAGE,
  firestore,
  storage,
  functions,
  STORAGE_URL,
} from '../../../../services/Firebase';
import { strings } from '../../../../strings';
import { checkFieldExist } from '../../../../services';
import { COUNTRIES } from '../../../../app-constants/countries';
import { DeleteIcon } from '../../../../assets/Modal';
import UploadImage from '../../../../shared/Components/UploadImage/UploadImage';
import { normalizeString } from '../../../../utilities/normalizeString';

interface FormInputs {
  [RegistrationField.Name]: string;
  [RegistrationField.Email]: string;
  [RegistrationField.Password]: string;
  [RegistrationField.ConfirmPassword]: string;
  [RegistrationField.RevenueShare]: number;
  [RegistrationField.City]: string;
  [RegistrationField.Country]: any;
  [RegistrationField.Description]: string;
  [RegistrationField.DisplayName]: string;
  [RegistrationField.ProfileLink]: string;
}

const FormContainerNewAccount = styled.form`
  margin-top: 50px;
`;

function CreateArtistScreen() {
  const usersRef = collection(firestore, 'usersWeb');
  const contriesOptions = COUNTRIES.map((item) => ({ label: item, value: item }));
  const usersQuery = useFirestoreQuery(
    ['usersWeb'],
    usersRef,
    {
      subscribe: true,
      includeMetadataChanges: true,
    },
    {
      refetchOnMount: 'always',
    },
  );

  // State that allows tracking progress form submition.
  // Currently used for showing loading indicator on submit button,
  // so user has feedback of what is going on.
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [passwordMap, setPasswordMap] = useState<{ password: string; confirm: string }>({
    password: '',
    confirm: '',
  });

  const { colors } = useTheme();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setError,
  } = useForm<FormInputs>({
    schema: registrationShema,
  });

  const [profileImage, setProfileImage] = useState<any>();
  const { getRootProps, isFocused } = useDropzone({
    onDrop: (files) => getImage(files, setProfileImage),
    maxSize: 10480000,
  });

  const [coverImage, setCoverImage] = useState<any>();
  const { getRootProps: coverProps, isFocused: coverFocused } = useDropzone({
    onDrop: (files) => getImage(files, setCoverImage),
    maxSize: 10480000,
  });

  const getImage = async (files: File[], setterFunction: any) => {
    const file = files[0];
    setterFunction(file);
  };

  const submitImageUpload = async (
    id: string,
    image: File,
    storageName: string,
    dimensions: { width: number; height: number },
  ) => {
    try {
      if (image) {
        const canvas = await loadImageOnCanvas(URL.createObjectURL(image));
        const originalImageReference = ref(storage, `users/${id}/original_${storageName}`);
        const imageReference = ref(storage, `users/${id}/${storageName}`);
        await uploadBytes(originalImageReference, image);
        const uploadResult = await uploadBytes(
          imageReference,
          await canvasToBuffer(
            canvas.width > dimensions.width || canvas.height > dimensions.height
              ? await processImage(
                  resizeAndCropToKeepAspect,
                  canvas,
                  dimensions.width,
                  dimensions.height,
                )
              : canvas,
            ImageFormat.JPEG,
            1,
          ),
          { contentType: ImageFormat.JPEG },
        );

        return `${STORAGE_URL}/${uploadResult.ref.fullPath}?timestamp=${new Date().getTime()}`;
      }
    } catch (error) {
      console.log(error);
      toast.error(strings.toastNotificationSomethingWrong);
    }
  };

  const onSubmit = handleSubmit(async (data: FormInputs) => {
    const refUsers = collection(firestore, 'usersWeb');
    const check = await checkFieldExist(refUsers, 'username', data.DisplayName);
    if (check) {
      setError(RegistrationField.DisplayName, {
        type: 'custom',
        message: strings.usernameExist,
      });
      return;
    }
    const checkEmailTaken = await checkFieldExist(refUsers, 'email', data[RegistrationField.Email]);
    if (checkEmailTaken) {
      setError(RegistrationField.Email, {
        type: 'custom',
        message: 'Email already taken!',
      });
      return;
    }

    if (data.confirmPassword !== data.PasswordField) {
      toast.error('Passwords not matching!');
      return;
    }

    if (await checkFieldExist(refUsers, 'profileLink', data.profileLink)) {
      setError(RegistrationField.ProfileLink, {
        type: 'custom',
        message: 'Profile Link is already taken',
      });
      return;
    }

    const isDisplayNameValid = validateDisplayName(data.DisplayName);
    if (isDisplayNameValid.length) {
      isDisplayNameValid.forEach((message) =>
        setError(RegistrationField.DisplayName, {
          type: 'custom',
          message: message,
        }),
      );
      return;
    }

    setIsLoading(true);
    const createuser = httpsCallable(functions, 'createUser');

    try {
      const user: any = (await createuser({ email: data.EmailField, password: data.PasswordField }))
        .data;

      // Adds 'emailVerified' flag before user doc data is populated to make sure
      // hook function triggers in correct time
      await setDoc(doc(firestore, `usersWeb/${user}/private/accountFinalization`), {
        flags: ['emailVerified'],
      });

      await setDoc(doc(usersRef, user), {
        email: data.EmailField,
        Name: data.name?.trim(),
        Puzzles: 0,
        Followers: 0,
        revenueShare: data.revenueShare / 100,
        Role: RoleValue.ARTIST,
        uid: user,
        status: 'approved',
        isFirstLogin: false,
        isAcceptedTerms: false,
        City: data.city,
        Country: data.country.value,
        username: data.DisplayName?.trim(),
        normalizedUsername: normalizeString(data.DisplayName?.trim()),
        Description: data.description,
        profileLink: data.profileLink,
        profileImageUrl: profileImage
          ? await submitImageUpload(user, profileImage, `profile`, {
              width: PROFILE_IMAGE_WIDTH,
              height: PROFILE_IMAGE_HEIGHT,
            })
          : DEFAULT_PROFILE_IMAGE,
        coverImageUrl: coverImage
          ? await submitImageUpload(user, coverImage, `cover`, {
              width: COVER_IMAGE_WIDTH,
              height: COVER_IMAGE_HEIGHT,
            })
          : DEFAULT_COVER_IMAGE,
        createdAt: Timestamp.fromDate(Timestamp.now().toDate()),
      }).then(() => {
        toast.success(strings.toastNotificationSuccessAddAcc);
      });

      //await verifyEmail({ email: data.EmailField });

      setProfileImage(null);
      setCoverImage(null);

      reset();
    } catch (err) {
      if (err == 'FirebaseError: INTERNAL') {
        toast.error(strings.emailTaken);
      } else {
        console.log(err);
        toast.error(strings.somethingWentWrong);
      }
      return;
    }
    setIsLoading(false);
  });

  if (usersQuery.isLoading) {
    return <Splash />;
  }

  return (
    <>
      <Box margin="150px auto 50px auto">
        <Text size={2} weight="bold">
          {strings.createArtistTitle}
        </Text>
        <FormContainerNewAccount onSubmit={onSubmit}>
          <FlexContainer flexDirection="column">
            <FlexContainer flexWrap="wrap" justifyContent="space-between" padding="15px">
              <FlexContainer flexDirection="column" width="250px" margin="0 auto 40px">
                {!profileImage ? (
                  <DropZoneContainer {...getRootProps({ isFocused })}>
                    <UploadImage buttonText={strings.uploadProfileImage} />
                  </DropZoneContainer>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <AbsoluteImage
                      top={0}
                      right={0}
                      left={0}
                      bottom={0}
                      cursor="pointer"
                      src={DeleteIcon}
                      alt="remove image"
                      onClick={() => setProfileImage(null)}
                    />
                    <Image
                      style={{
                        objectFit: 'cover',
                        objectPosition: 'center',
                        height: '200px',
                        width: '200px',
                        marginLeft: '20px',
                        marginTop: '20px',
                      }}
                      borderRadiusType="circle"
                      src={URL.createObjectURL(profileImage)}
                    />
                  </div>
                )}
              </FlexContainer>
              <FlexContainer flexDirection="column" margin="0 auto 40px" flex="1">
                {!coverImage ? (
                  <DropZoneContainer {...coverProps({ coverFocused })}>
                    <UploadImage buttonText={strings.uploadCoverImage} />
                  </DropZoneContainer>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <AbsoluteImage
                      top={0}
                      right={0}
                      left={0}
                      bottom={0}
                      cursor="pointer"
                      src={DeleteIcon}
                      alt="remove image"
                      onClick={() => setCoverImage(null)}
                    />
                    <Image
                      style={{
                        marginTop: '40px',
                        objectFit: 'cover',
                        objectPosition: 'center',
                      }}
                      borderRadiusType="rounded"
                      src={URL.createObjectURL(coverImage)}
                      width="100%"
                      height="150px"
                    />
                  </div>
                )}
              </FlexContainer>
            </FlexContainer>
            <FlexContainer flexWrap="wrap" justifyContent="space-between">
              <Input
                required
                placeholder={strings.createAccNamePlaceholder}
                name={RegistrationField.Name}
                type="text"
                control={control}
                error={errors[RegistrationField.Name]}
                placeholderIcon={User}
                width="400px"
                marginBottom={20}
                marginRight={30}
                margin="0 auto 40px"
              />
              <Input
                required
                placeholder={strings.createAccEmailPlaceholder}
                name={RegistrationField.Email}
                type="email"
                control={control}
                error={errors[RegistrationField.Email]}
                placeholderIcon={User}
                width="400px"
                marginBottom={20}
                marginRight={30}
                margin="0 auto 40px"
              />
            </FlexContainer>
            <FlexContainer flexDirection="column" flexWrap="wrap" justifyContent="space-between">
              <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
                <Input
                  required
                  placeholder={strings.createAccPasswordPlaceholder}
                  name={RegistrationField.Password}
                  type="password"
                  control={control}
                  error={errors[RegistrationField.Password]}
                  placeholderIcon={Password}
                  width="400px"
                  marginBottom={20}
                  autoComplete={false}
                  marginRight={30}
                  margin="0 auto 40px"
                  onChangeHook={(event: any) =>
                    setPasswordMap({ ...passwordMap, password: event.target.value })
                  }
                />
                <Input
                  required
                  placeholder={strings.createAccConfirmPasswordPlaceholder}
                  name={RegistrationField.ConfirmPassword}
                  type="password"
                  control={control}
                  error={errors[RegistrationField.ConfirmPassword]}
                  placeholderIcon={Password}
                  width="400px"
                  marginBottom={20}
                  autoComplete={false}
                  marginRight={30}
                  margin="0 auto 40px"
                  onChangeHook={(event: any) =>
                    setPasswordMap({ ...passwordMap, confirm: event.target.value })
                  }
                />
              </FlexContainer>
              {passwordMap.password !== passwordMap.confirm && (
                <Text padding="5px 0" textAlign="center" color="red">
                  {strings.createAccPasswordMatch}
                </Text>
              )}
            </FlexContainer>
            <FlexContainer margin="20px 0 0 0" flexWrap="wrap" justifyContent="space-between">
              <SelectForm
                required
                placeholder={strings.selectCountry}
                name={RegistrationField.Country}
                control={control}
                values={contriesOptions}
                error={errors[RegistrationField.Country]}
                width="400px"
                margin="0 auto 40px"
              />
              <Input
                required
                placeholder={strings.city}
                name={RegistrationField.City}
                type="text"
                control={control}
                error={errors[RegistrationField.City]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                marginRight={30}
                margin="0 auto 40px"
              />
            </FlexContainer>
            <FlexContainer flexWrap="wrap" justifyContent="space-between">
              <Input
                required
                placeholder={strings.displayName}
                name={RegistrationField.DisplayName}
                type="text"
                control={control}
                error={errors[RegistrationField.DisplayName]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                marginRight={30}
                margin="0 auto 40px"
              />
              <Input
                required
                placeholder={strings.revenueShare}
                name={RegistrationField.RevenueShare}
                type="number"
                defaultValue={100}
                control={control}
                error={errors[RegistrationField.RevenueShare]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                marginRight={30}
                margin="0 auto 40px"
                interval={{
                  min: 0,
                  max: 100,
                }}
              />
            </FlexContainer>

            <FlexContainer flexWrap="wrap" justifyContent="space-between">
              <Input
                required
                placeholder={'Profile link'}
                name={RegistrationField.ProfileLink}
                type="text"
                control={control}
                error={errors[RegistrationField.ProfileLink]}
                width="400px"
                marginBottom={20}
                autoComplete={false}
                marginRight={30}
                margin="0 auto 40px"
              />
            </FlexContainer>
            <FlexContainer>
              <Input
                placeholder={strings.description}
                name={RegistrationField.Description}
                type="textarea"
                control={control}
                error={errors[RegistrationField.Description]}
                width="400px"
                height="200px"
                marginBottom={20}
                autoComplete={false}
                marginRight={30}
                margin="0 auto 40px"
              />
            </FlexContainer>
            <FlexContainer justifyContent="flex-end">
              <Button width={400} height={58} type="submit" disabled={isLoading ? true : undefined}>
                {isLoading ? (
                  <Splash width={35} height={35} color={colors.white} />
                ) : (
                  strings.submit
                )}
              </Button>
            </FlexContainer>
          </FlexContainer>
        </FormContainerNewAccount>
      </Box>
    </>
  );
}

export default CreateArtistScreen;
